import { CompactPagination } from "@/components/Pagination";
import { ActionTitle } from "@/components/actionTitle/ActionTitle";
import { useSearchProject } from "@/features/projects/SearchProject";
import styled from "@emotion/styled";
import {
  type OpportunitySearch,
  ProjectFilterDefault,
  type WidgetSetting,
  type WidgetSize,
  useProfile,
} from "@repo/api";
import { Text, Variables } from "@repo/skapa";
import { t } from "@repo/translation";
import { useEffect, useState } from "react";

import { ScrollContainer, WidgetContainer } from "../Components";
import { CARD_HEIGHT_NORMAL } from "../constants";
import { GeneratedProjectDetailModal } from "./GeneratedProjectDetailModal";
import { GeneratedProjectsTable } from "./GeneratedProjectsTable";

interface GeneratedProjectsWidgetProps {
  title: string;
  settings?: WidgetSetting;
  size: WidgetSize;
}

const Summary = styled.div`
  border-bottom: 1px solid ${Variables.colourNeutral3};
  padding: ${Variables.space50} 0 ${Variables.space100} 0;
`;

export const GeneratedProjectsWidget = ({
  title,
}: GeneratedProjectsWidgetProps) => {
  const { data: profile } = useProfile();
  const [selectedProject, setSelectedProject] = useState<
    OpportunitySearch | undefined
  >(undefined);
  const {
    result: projects,
    totalSize,
    size,
    setSize,
    isValidating,
    actions,
  } = useSearchProject();

  useEffect(() => {
    if (!profile?.mail) {
      return;
    }
    actions.setAllFilters({
      ...ProjectFilterDefault,
      isWidgetSearch: true,
      followupAssignee: profile?.mail,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.mail]);

  return (
    <WidgetContainer size={CARD_HEIGHT_NORMAL}>
      <ActionTitle title={`${title} (${totalSize})`} />

      <Summary>
        <Text textStyle="Heading.XS">
          {t("prompt.generated_projects_prompt")}
        </Text>
      </Summary>

      {totalSize === 0 && !isValidating && (
        <>
          <Text>{t("prompt.no_projects_to_follow_up")}</Text>
          <Text>{t("prompt.generate_projects_instruction")}</Text>
        </>
      )}
      <ScrollContainer>
        <GeneratedProjectsTable
          isLoading={isValidating}
          projects={projects}
          onClickRow={(project) => setSelectedProject(project)}
        />
      </ScrollContainer>
      <CompactPagination
        setSize={setSize}
        size={size}
        totalNumber={totalSize ?? 0}
        currentNumber={projects.length}
      />
      <GeneratedProjectDetailModal
        isOpen={selectedProject !== undefined}
        onClose={() => setSelectedProject(undefined)}
        project={selectedProject}
      />
    </WidgetContainer>
  );
};
